import type { DataTableColumn } from 'mantine-datatable';
import { useCallback, useMemo, useState } from 'react';

import { useCurrencyQueryActions } from '@/api/hooks/currencyQueryActions';
import { useEstimationCostQueryActions } from '@/api/hooks/estimationCostQueryActions';
import type {
  CurrencyResponse,
  EstimationCostResponse,
  UpsertEstimationCostRequest,
} from '@/api/schema/data-contracts';
import { ApiEstimationItemPurpose, ApiEstimationStage } from '@/api/schema/data-contracts';
import { AssumptionsModal } from '@/components/Assumption';
import { Stack } from '@/components/atoms/Stack';
import TableSkeleton from '@/components/atoms/TableSkeleton';
import Search from '@/components/molecules/Search';
import { CommentsModal } from '@/components/organism/Comments/CommentsModal';
import { TableColumnsSelector } from '@/components/organism/DataTable/components/columnsSelector';
import { ExportButtonsSelector } from '@/components/organism/DataTable/components/exportSelector';
import { TablePhasesSelector } from '@/components/organism/DataTable/components/phasesSelector';
import { eExportFormat } from '@/components/organism/DataTable/model';
import type { EstimationExportButtonsProps } from '@/components/organism/EstimationExportButtons';
import { messages as sidebarMessageDefinitions } from '@/components/organism/Sidebar/messages';
import { useMessages } from '@/hooks/messages';
import { useEstimationExport } from '@/hooks/useEstimationExport';
import { useHasEstimationPermission } from '@/hooks/useHasEstimationPermission';
import type { EstimationItemsGroupedTableProps } from '@/pages/estimation/detail/EstimationItemsGroupedTables';
import { Route } from '@/routes/estimation/detail/$estimationId/$tabId';
import type { OmitSafely } from '@/types';
import { getPhaseAndDescendantIds } from '@/utils/phasesFiltering';
import { sortCostTypes, sortResourceTypes } from '@/utils/sorting';

import EstimationItemDetailModal from '../../EstimationItemDetailModal';
import { Layout } from '../../Layout';
import { getResourceEstimationItemDetail } from '../../mappers/estimationItemDetailMapper';
import { estimationTabMessageDefinitions } from '../../messages';
import type {
  CostModel,
  EstimationItemActionCallback,
  ResourceEstimationItemsTableRecord,
} from '../../model';
import { useResourceEstimationItemsTableRecords } from '../../useEstimationItemsTableRecords';
import { useResourceDisplayNameRename } from '../../useResourceDisplayNameRename';
import { CostingTables } from './CostingTables';
import { CostPerUnitForm } from './CostPerUnitForm';
import { resourceCostsMessageDefinitions } from './messages';
import type { CostPerUnitFormModel } from './model';
import SummaryTable from './SummaryTable';

const emptyArray: Array<ResourceEstimationItemsTableRecord> = [];

const EstimationDetailResourceCostTab = () => {
  const costsMessages = useMessages(resourceCostsMessageDefinitions);
  const sidebarMessages = useMessages(sidebarMessageDefinitions);
  const tabMessages = useMessages(estimationTabMessageDefinitions);
  const { estimationId } = Route.useLoaderData();
  const [searchText, setSearchText] = useState('');
  const [hiddenCostTypeColumns, setHiddenCostTypeColumns] = useState<string[]>([]);
  const [hiddenResourcePhases, setHiddenResourcePhases] = useState<string[]>([]);

  const {
    data,
    isLoading,
    getResourceEstimationItemsTableRecordsByParent: getEstimationItemsTableRecordsByParent,
  } = useResourceEstimationItemsTableRecords(estimationId, { shouldFetchResources: true });

  const { useGetEstimationCosts, useUpdateEstimationCosts } = useEstimationCostQueryActions();
  const { data: estimationCost, isLoading: isLoadingEstimationCost } = useGetEstimationCosts(
    estimationId,
  ) as { data: EstimationCostResponse | '' | undefined; isLoading: boolean };
  const updateEstimationCostsMutation = useUpdateEstimationCosts();

  const { useGetCurrencies } = useCurrencyQueryActions();
  const { data: currencies, isLoading: isLoadingCurrencies } = useGetCurrencies() as {
    data: CurrencyResponse[] | undefined;
    isLoading: boolean;
  };

  const hasEditPermission = useHasEstimationPermission(estimationId, 'CAN_EDIT');

  const [estimationItemIdForAssumptionsModal, setEstimationItemIdForAssumptionsModal] =
    useState<string>();
  const [estimationItemIdsForDetailsModal, setEstimationItemIdsForDetailsModal] = useState<
    string[]
  >([]);
  const [estimationItemIdForCommentsModal, setEstimationItemIdForCommentsModal] =
    useState<string>();

  const topLevelEstimationItems = useMemo(
    () => getEstimationItemsTableRecordsByParent(undefined).filter((item) => !item.isOmitted),
    [getEstimationItemsTableRecordsByParent],
  );

  // Filter out hidden resource phases and their descendants
  const filteredTopLevelEstimationItems = useMemo(() => {
    if (!data || hiddenResourcePhases.length === 0) {
      return topLevelEstimationItems;
    }

    const hiddenPhaseAndDescendantIds = new Set<string>();
    hiddenResourcePhases.forEach((phaseId) => {
      const ids = getPhaseAndDescendantIds(phaseId, data.estimationItems);
      ids.forEach((id) => hiddenPhaseAndDescendantIds.add(id));
    });

    return topLevelEstimationItems.filter((item) => !hiddenPhaseAndDescendantIds.has(item.id));
  }, [topLevelEstimationItems, hiddenResourcePhases, data]);

  // Get resource phases for the selector (top-level RESOURCE items)
  const resourcePhasesForSelector = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.estimationItems
      .filter((item) => !item.parentId && item.purpose === ApiEstimationItemPurpose.RESOURCE)
      .map((item) => ({
        id: item.id,
        name: item.name,
      }));
  }, [data]);

  const calculatedResultByEstimationItemId = useMemo(
    () =>
      new Map<string, number>(
        data?.estimationItems.map((item) => [item.id, item.calculatedResult ?? 0]),
      ),
    [data?.estimationItems],
  );

  const sortedResourceTypes = useMemo(
    () =>
      data
        ? sortResourceTypes(
            data.estimationModel.resourceTypes,
            data.estimationModel.resourceTypeGroups,
          )
        : [],
    [data],
  );

  // EWM-738: allow renaming resource-type column headers on the Resource Cost screen, persisting
  // the per-estimation display-name overrides via the same path as the Resourcing tab.
  const {
    effectiveResourceDisplayNames,
    onResourceDisplayNameRename,
    hasPersistedOverrides,
    resetResourceDisplayNames,
  } = useResourceDisplayNameRename({
    estimation: data?.estimation,
    projectName: data?.project.name,
    sortedResourceTypes,
  });

  const costModel = useMemo<CostModel | undefined>(() => {
    if (estimationCost === undefined || data?.estimationModel.currencyIds === undefined) {
      return undefined;
    }

    const allResourceTypes = sortResourceTypes(
      data.estimationModel.resourceTypes,
      data.estimationModel.resourceTypeGroups,
    );

    if (estimationCost === '') {
      return {
        currencyId: data.estimationModel.currencyIds[0],
        resourceTypeCosts: allResourceTypes.map((resourceType) => ({
          resourceTypeId: resourceType.id,
          costTypeId: resourceType.costTypeId,
          price: resourceType.resourceCost ?? 0,
        })),
      };
    }

    return {
      currencyId: estimationCost.currencyId,
      resourceTypeCosts: allResourceTypes.map((resourceType) => {
        const storedCost = estimationCost.resourceTypeCosts.find(
          (resourceTypeCost) => resourceTypeCost.resourceTypeId === resourceType.id,
        );
        return {
          resourceTypeId: resourceType.id,
          costTypeId: resourceType.costTypeId,
          price:
            storedCost === undefined || storedCost.updatedAt === null
              ? (resourceType.resourceCost ?? 0)
              : storedCost.price,
        };
      }),
    };
  }, [estimationCost, data]);

  const exportProps = useMemo<OmitSafely<EstimationExportButtonsProps, 'parentEstimationItemId'>>(
    () => ({
      estimationId,
      title: `${sidebarMessages.detailedEstimation()} - ${tabMessages.resourceCostLabel()}`,
      costModel,
      outputOptionsArray: [
        {
          showResourcePurposeEstimationItems: true,
          showEstimateColumn: true,
          showResourceCostColumns: true,
          showCostSumColumn: true,
        },
      ],
      hiddenCostTypeColumns,
      hiddenPhases: hiddenResourcePhases,
    }),
    [
      estimationId,
      costModel,
      sidebarMessages,
      tabMessages,
      hiddenCostTypeColumns,
      hiddenResourcePhases,
    ],
  );

  const estimationExporter = useEstimationExport(exportProps);
  const handleExportButtonClick = useCallback(
    (format: eExportFormat) => estimationExporter(format, undefined),
    [estimationExporter],
  );

  const exportConfiguration = useMemo<eExportFormat[]>(
    () => [
      eExportFormat.EXCEL,
      eExportFormat.PDF,
      eExportFormat.CSV,
      eExportFormat.JSON,
      eExportFormat.PRINT,
    ],
    [],
  );

  const costTypeColumns: DataTableColumn<{ id: string }>[] = useMemo(() => {
    if (!data?.estimationModel) {
      return [];
    }
    const allCostTypes = sortCostTypes(
      data.estimationModel.costTypes,
      data.estimationModel.costTypeGroups,
    );
    return allCostTypes.map((costType) => ({
      accessor: costType.id,
      title: costType.name,
    }));
  }, [data]);

  const handleColumnsChange = useCallback(
    (visibleColumns: DataTableColumn<{ id: string }>[]) => {
      const visibleAccessors = new Set(visibleColumns.map((col) => col.accessor as string));
      const allAccessors = costTypeColumns.map((col) => col.accessor as string);
      const hidden = allAccessors.filter((accessor) => !visibleAccessors.has(accessor));
      setHiddenCostTypeColumns(hidden);
    },
    [costTypeColumns],
  );

  if (isLoading || isLoadingEstimationCost || isLoadingCurrencies) {
    return (
      <Layout>
        <h2 className='mt-5'>{costsMessages.costPerUnitTitle()}</h2>
        <TableSkeleton />
        <h2 className='mt-5'>{costsMessages.costBreakDownTitle()}</h2>
        <TableSkeleton />
      </Layout>
    );
  }

  if (data === undefined || costModel === undefined || currencies === undefined) {
    return (
      <Layout>
        <TableSkeleton />
      </Layout>
    );
  }

  if (!data.resources) {
    throw new Error('Resources cannot be undefined.');
  }

  const isReadOnly =
    data.estimation.stage === ApiEstimationStage.IN_REVIEW ||
    data.estimation.stage === ApiEstimationStage.APPROVED ||
    !hasEditPermission;

  const onAssumptionsClicked: EstimationItemActionCallback = setEstimationItemIdForAssumptionsModal;
  const detailsButtonClickedCallback: EstimationItemActionCallback = (estimationItemId: string) => {
    setEstimationItemIdsForDetailsModal([...estimationItemIdsForDetailsModal, estimationItemId]);
  };
  const onCommentsClicked: EstimationItemActionCallback = setEstimationItemIdForCommentsModal;

  const tableProps: OmitSafely<
    EstimationItemsGroupedTableProps,
    'estimationItems' | 'control' | 'searchText' | 'formModelEstimationItems'
  > = {
    estimationModel: data.estimationModel,
    methodologies: data.methodologies,
    costs: costModel,
    showButtons: true,
    showMethodology: false,
    showResponsible: false,
    showParameters: false,
    showCosts: true,
    isReadOnly: true,
    onIncludeClicked: () => Promise.reject(new Error('Read-only')),
    estimationUnit: data.estimation.unit,
    onAssumptionsClicked,
    onCommentsClicked,
    onDetailsClicked: detailsButtonClickedCallback,
    calculatedResultByEstimationItemId,
    hiddenCostTypeColumns,
  };

  const onSubmit = (formModel: CostPerUnitFormModel) => {
    const updateEstimationCostsData: UpsertEstimationCostRequest = {
      currencyId: formModel.currencyId,
      resourceTypeCosts: formModel.resourceTypeCosts.map((x) => ({
        resourceTypeId: x.resourceTypeId,
        price: x.price ?? 0,
      })),
    };
    updateEstimationCostsMutation.mutate({
      estimationId,
      updateEstimationCostsData,
    });
  };

  return (
    <Layout>
      <Stack gap={10}>
        <div className='flex items-center gap-2'>
          <h2>{costsMessages.costPerUnitTitle()}</h2>
          {!isReadOnly && hasPersistedOverrides && (
            <button
              type='button'
              className='text-sm underline'
              onClick={() => {
                if (!hasPersistedOverrides) return;
                void resetResourceDisplayNames();
              }}
            >
              {tabMessages.resetResourceNamesToDefaults()}
            </button>
          )}
        </div>
        <CostPerUnitForm
          currencies={currencies}
          estimationModel={data.estimationModel}
          estimationCost={costModel}
          resourceDisplayNames={effectiveResourceDisplayNames}
          onSubmit={onSubmit}
          isReadOnly={isReadOnly}
          onResourceColumnHeaderRename={isReadOnly ? undefined : onResourceDisplayNameRename}
        />
        <div className='mt-4 flex items-center justify-between gap-2'>
          <h2 className='mt-5'>{costsMessages.costBreakDownTitle()}</h2>
          <div className='flex gap-2'>
            <Search handleOnChange={setSearchText} />
            <ExportButtonsSelector
              exportConfiguration={exportConfiguration}
              onExportButtonClick={handleExportButtonClick}
            />
            {resourcePhasesForSelector.length > 0 && (
              <TablePhasesSelector
                phases={resourcePhasesForSelector}
                hiddenPhases={hiddenResourcePhases}
                onPhasesChange={setHiddenResourcePhases}
              />
            )}
            {costTypeColumns.length > 0 && (
              <TableColumnsSelector
                columns={costTypeColumns}
                onColumnsChange={handleColumnsChange}
                hiddenColumns={hiddenCostTypeColumns}
              />
            )}
          </div>
        </div>

        <CostingTables
          data={data}
          dataPending={updateEstimationCostsMutation.isPending}
          estimationCost={costModel}
          tableProps={{ ...tableProps, searchText }}
          topLevelEstimationItems={filteredTopLevelEstimationItems}
        />
        <SummaryTable
          estimationItems={filteredTopLevelEstimationItems}
          estimationModel={data.estimationModel}
          costs={costModel}
          showCosts
        />
      </Stack>
      {estimationItemIdsForDetailsModal.map((estimationItemIdForDetail) => (
        <EstimationItemDetailModal
          key={estimationItemIdForDetail}
          open={!!estimationItemIdForDetail}
          onClose={() =>
            setEstimationItemIdsForDetailsModal([...estimationItemIdsForDetailsModal.slice(0, -1)])
          }
          onSubmit={() => Promise.resolve()}
          shouldResetForm={true}
          onFormReset={() => {}}
          estimationItem={
            estimationItemIdForDetail === undefined
              ? undefined
              : getResourceEstimationItemDetail(estimationItemIdForDetail, data)
          }
          estimationItems={
            estimationItemIdForDetail
              ? getEstimationItemsTableRecordsByParent(estimationItemIdForDetail)
              : emptyArray
          }
          estimationMethodologyId={data.estimation.methodologyId}
          estimationCultureName={data.estimation.cultureName}
          exportProps={exportProps}
          {...tableProps}
        />
      ))}
      <AssumptionsModal
        estimationId={estimationId}
        onClose={() => setEstimationItemIdForAssumptionsModal(undefined)}
        estimationItem={data.estimationItems.find(
          (x) => x.id === estimationItemIdForAssumptionsModal,
        )}
      />
      <CommentsModal
        commentType='ESTIMATION_ITEM'
        commentContext='ESTIMATION'
        size='s'
        estimationId={data.estimation.id}
        estimationItem={data.estimationItems.find((x) => x.id === estimationItemIdForCommentsModal)}
        onClose={() => setEstimationItemIdForCommentsModal(undefined)}
      />
    </Layout>
  );
};

export default EstimationDetailResourceCostTab;
